import { z } from "zod";
import { BASE, HUE, ISO, ISO_DATE, MONEY } from "./common.ts";

export const COMPLIANCE = z.enum(["complete", "expiring", "missing"]);
export type Compliance = z.infer<typeof COMPLIANCE>;

export const FREELANCER_DOCUMENT = z.object({ kind: z.string(), expiresAt: ISO.optional() });
export type FreelancerDocument = z.infer<typeof FREELANCER_DOCUMENT>;

/** One key per address, so the same link written two ways counts once. */
const linkKey = (url: string) => {
  try {
    return new URL(url).href;
  } catch {
    return url;
  }
};

/** A piece of past work a freelancer shows the team: a title and a secure link, https only (X11-625b). */
export const PORTFOLIO_ITEM = z.strictObject({
  title: z.string().trim().min(1).max(120),
  url: z.url({ protocol: /^https$/, error: "Use a secure link for the portfolio item." }).max(500),
});
export type PortfolioItem = z.infer<typeof PORTFOLIO_ITEM>;

/** Up to 20 items; a link may appear only once. */
export const PORTFOLIO = z.array(PORTFOLIO_ITEM).max(20).superRefine((items, ctx) => {
  const seen = new Set<string>();
  items.forEach((item, i) => {
    const key = linkKey(item.url);
    if (seen.has(key)) ctx.addIssue({ code: "custom", path: [i, "url"], message: "This link is already in the portfolio." });
    seen.add(key);
  });
});

/** One answer set from the classification questionnaire (IR35 or AB5), with the result it gave and the risk of that result. */
export const CLASSIFICATION = z.object({
  id: z.string(),
  at: ISO,
  regime: z.enum(["ir35", "ab5", "us1099"]),
  answers: z.record(z.string(), z.boolean()),
  result: z.enum(["employee", "contractor", "review"]),
  risk: z.enum(["low", "medium", "high"]),
  reasons: z.array(z.string()),
  by: z.string(),
});
export type Classification = z.infer<typeof CLASSIFICATION>;

/** A contractor on the bench. `availability` holds 12 numbers: hours free per week, for the next 12 weeks. */
export const FREELANCER = BASE.extend({
  name: z.string(),
  title: z.string(),
  skills: z.array(z.string()),
  rate: MONEY,
  /** The hourly, daily and fixed rates (X11-625a). `rate` stays the default: the hourly rate when there is one. */
  rates: z.strictObject({ hourly: MONEY.optional(), daily: MONEY.optional(), fixed: MONEY.optional() }).optional(),
  /** Links to past work the team can show, each a title and a secure link (X11-625b). */
  portfolio: PORTFOLIO.default([]),
  hue: HUE,
  timeZone: z.string(),
  availability: z.array(z.number()).length(12),
  rating: z.number().min(1).max(5),
  compliance: COMPLIANCE,
  documents: z.array(FREELANCER_DOCUMENT),
  marginPct: z.number(),
  country: z.string(),
  /** Person id of the workspace member this freelancer signs in as, when there is one. */
  personId: z.string().optional(),
  /** Identity check from onboarding: the method and the provider's reference. The document itself is never stored (X2-13). */
  idCheck: z.object({ method: z.string(), reference: z.string(), checkedAt: ISO }).optional(),
  /** Safe preview of the payout account (last four characters). The account itself is sealed in the vault. */
  payoutHint: z.string().optional(),
  /** How the freelancer chose to be paid on their onboarding link (X11-626a): a bank account or Wise. */
  payoutMethod: z.enum(["bank", "wise"]).optional(),
  /** Which payout rail pays this freelancer. */
  payoutRail: z.enum(["wise", "stripe", "paypal", "payoneer", "deel"]).optional(),
  /** Dated IR35 and AB5 determinations, newest last (X2-13). */
  classifications: z.array(CLASSIFICATION).optional(),
  /** Contract start and end, as YYYY-MM-DD (L2). Both are optional and cleared by sending null. */
  contractStart: ISO_DATE.nullable().optional(),
  contractEnd: ISO_DATE.nullable().optional(),
});
export type Freelancer = z.infer<typeof FREELANCER>;

export const WORK_ORDER_STATE = z.enum(["draft", "active", "done", "declined"]);
export type WorkOrderState = z.infer<typeof WORK_ORDER_STATE>;

/** A freelancer's submission against a work order: hours for a timesheet, or an amount for a milestone (X2-13). */
export const WORK_SUBMISSION = z.object({
  id: z.string(),
  kind: z.enum(["timesheet", "milestone"]),
  at: ISO,
  note: z.string(),
  hours: z.number().optional(),
  amount: MONEY.optional(),
  state: z.enum(["submitted", "approved", "rejected"]),
  reason: z.string().optional(),
  payoutId: z.string().optional(),
});
export type WorkSubmission = z.infer<typeof WORK_SUBMISSION>;

/** A freelancer's scoped piece of work on a project, capped in hours. */
export const WORK_ORDER = BASE.extend({
  freelancerId: z.string(),
  projectId: z.string(),
  title: z.string(),
  capHours: z.number(),
  usedHours: z.number(),
  rate: MONEY,
  state: WORK_ORDER_STATE,
  /** Why the freelancer turned the work order down. Set when `state` is `declined`. */
  declineReason: z.string().optional(),
  /** Timesheet and milestone submissions, newest last. Pending hours count against the cap (X2-13). */
  submissions: z.array(WORK_SUBMISSION).optional(),
  /** The requisition this work order fills. */
  requisitionId: z.string().optional(),
  /** The agreement this order is signed under. Nobody can submit hours or a milestone until it is signed (X11-627a). */
  agreementId: z.string().optional(),
  /** The team's rating of the finished job, 1 to 5. Staff only; it moves the freelancer's `rating` (X9-196a). */
  rating: z.number().int().min(1).max(5).optional(),
  /** Internal note on the rating. Staff only. */
  ratingNote: z.string().max(500).optional(),
  /** When the rating was last given or changed. */
  ratedAt: ISO.optional(),
});
export type WorkOrder = z.infer<typeof WORK_ORDER>;

export const PAYOUT_STATE = z.enum(["pending", "approved", "paid", "failed", "rejected"]);
export type PayoutState = z.infer<typeof PAYOUT_STATE>;

/** A payment to a freelancer for one period (`2026-09` style). `invoiceRef` is the self-billing invoice number. */
export const PAYOUT = BASE.extend({
  freelancerId: z.string(),
  amount: MONEY,
  period: z.string(),
  state: PAYOUT_STATE,
  invoiceRef: z.string(),
  /** The work order whose approved submission produced this payout. */
  workOrderId: z.string().optional(),
  /** The payout rail that sent it, and the provider's own reference once it is sent (X2-13). */
  rail: z.enum(["wise", "stripe", "paypal", "payoneer", "deel"]).optional(),
  providerRef: z.string().optional(),
  /** The supplier bill this payout raised when it was approved (one per payout, never a second). */
  billId: z.string().optional(),
  /** The approval that decides this payout, opened by a "freelancer-invoice" rule (X13-1410a). One per payout while it is pending. */
  approvalId: z.string().optional(),
});
export type Payout = z.infer<typeof PAYOUT>;

/** A question on a job's application form (A6c). `id` is stable across edits; `label` is the text the applicant reads. */
export const APPLY_QUESTION = z.object({ id: z.string().trim().min(1).max(40), label: z.string().trim().min(1).max(200), required: z.boolean() });
export type ApplyQuestion = z.infer<typeof APPLY_QUESTION>;

/** The public application form of one job (A6c). A job takes applications only when `open` is true and its state is open.
 *  `links` asks for portfolio or profile links, `videoUrl` asks for a video introduction (Loom or similar), `questions` are the
 *  agency's own questions and `certifications` is the checklist the applicant ticks. */
export const APPLY_CONFIG = z.object({
  open: z.boolean(),
  links: z.boolean(),
  videoUrl: z.boolean(),
  questions: z.array(APPLY_QUESTION).max(20),
  certifications: z.array(z.string().trim().min(1).max(120)).max(30),
});
export type ApplyConfig = z.infer<typeof APPLY_CONFIG>;

export const JOB = BASE.extend({
  title: z.string(),
  department: z.string(),
  kind: z.enum(["employee", "freelancer"]),
  state: z.enum(["open", "draft", "closed"]),
  applicants: z.number(),
  openedAt: ISO,
  ownerId: z.string(),
  /** The job's public application form (A6c). Unset until the agency turns the form on. */
  apply: APPLY_CONFIG.optional(),
});
export type Job = z.infer<typeof JOB>;

/** What an applicant sends from the public form (A6c). A link must be a full https or http address; an empty video link means none. */
const APPLY_LINK = z.string().trim().max(500).refine((u) => /^https?:\/\/\S+$/.test(u), "Use a full web address that starts with https://.");
export const APPLICATION_INPUT = z.strictObject({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(254),
  links: z.array(APPLY_LINK).max(5).default([]),
  videoUrl: z.string().trim().max(500).refine((u) => u === "" || /^https?:\/\/\S+$/.test(u), "Use a full web address that starts with https://.").default(""),
  answers: z.record(z.string().max(40), z.string().max(2000)).default({}),
  certifications: z.array(z.string().max(120)).max(30).default([]),
});
export type ApplicationInput = z.infer<typeof APPLICATION_INPUT>;

/** The built-in meaning of a hiring stage (X9-169a): the rules that book interviews and close candidates read this, whatever
 *  the agency calls the stage. */
export const CANDIDATE_STAGE_KIND = z.enum(["applied", "screen", "interview", "offer", "hired", "rejected"]);
export type CandidateStageKind = z.infer<typeof CANDIDATE_STAGE_KIND>;
/** A candidate's stage: the id of an entry of the workspace setting `candidateStages` (X9-169a). */
export const CANDIDATE_STAGE = z.string().trim().min(1).max(40);
export type CandidateStage = z.infer<typeof CANDIDATE_STAGE>;

/** A scheduled conversation with a candidate, held by one staff interviewer (X7-05c). */
export const INTERVIEW = z.object({ id: z.string(), at: ISO, interviewerId: z.string(), state: z.enum(["scheduled", "done", "cancelled"]) });
export type Interview = z.infer<typeof INTERVIEW>;

export const RECOMMENDATION = z.enum(["strong_yes", "yes", "no", "strong_no"]);
export type Recommendation = z.infer<typeof RECOMMENDATION>;

/** One interviewer's ratings of a candidate. `average` is the mean of the ratings, rounded to one decimal. */
export const SCORECARD = z.object({
  id: z.string(), interviewerId: z.string(), interviewId: z.string().optional(), at: ISO,
  ratings: z.array(z.object({ criterion: z.string().min(1).max(80), rating: z.number().int().min(1).max(5) })).min(1).max(12),
  recommendation: RECOMMENDATION, note: z.string().max(2000).optional(), average: z.number(),
});
export type Scorecard = z.infer<typeof SCORECARD>;

export const CANDIDATE = BASE.extend({
  jobId: z.string(),
  name: z.string(),
  stage: CANDIDATE_STAGE,
  score: z.number().optional(),
  source: z.string(),
  appliedAt: ISO,
  nextInterviewAt: ISO.optional(),
  interviews: z.array(INTERVIEW).optional(),
  scorecards: z.array(SCORECARD).optional(),
  /** The talent pool entry made when the candidate was hired onto a freelancer job (X9-171a). */
  freelancerId: z.string().optional(),
  /** What the applicant sent from the job's public application form (A6c); unset for candidates added by hand. */
  email: z.string().max(254).optional(),
  links: z.array(z.string().max(500)).max(5).optional(),
  videoUrl: z.string().max(500).optional(),
  answers: z.record(z.string().max(40), z.string().max(2000)).optional(),
  certifications: z.array(z.string().max(120)).max(30).optional(),
});
export type Candidate = z.infer<typeof CANDIDATE>;

export const AGENDA_ITEM = z.object({ id: z.string(), text: z.string(), by: z.string(), done: z.boolean() });
export type AgendaItem = z.infer<typeof AGENDA_ITEM>;

export const FOLLOW_UP = z.object({ text: z.string(), done: z.boolean() });
export type FollowUp = z.infer<typeof FOLLOW_UP>;

/** A recurring manager and report conversation. `personId` is the report, `managerId` the manager. */
export const ONE_ON_ONE = BASE.extend({
  personId: z.string(),
  managerId: z.string(),
  nextAt: ISO,
  agenda: z.array(AGENDA_ITEM),
  notes: z.string(),
  followUps: z.array(FOLLOW_UP),
});
export type OneOnOne = z.infer<typeof ONE_ON_ONE>;

export const SKILL_SCORE = z.object({ personId: z.string(), skill: z.string(), levels: z.array(z.number().int().min(0).max(4)) });
export type SkillScore = z.infer<typeof SKILL_SCORE>;

export const REVIEW_CYCLE_STATE = z.enum(["planned", "active", "closed"]);
export type ReviewCycleState = z.infer<typeof REVIEW_CYCLE_STATE>;

export const REVIEW_CYCLE_REVIEWER = z.enum(["manager", "peer", "self"]);
export type ReviewCycleReviewer = z.infer<typeof REVIEW_CYCLE_REVIEWER>;

export const REVIEW_CYCLE = BASE.extend({
  name: z.string(),
  state: REVIEW_CYCLE_STATE,
  dueAt: ISO,
  completed: z.number(),
  total: z.number(),
  skills: z.array(SKILL_SCORE),
  /** L1 cycle plan, all optional so older cycles stay valid: the period the cycle covers, the people it reviews (each one is a per-person review), who reviews them, and the questions. */
  periodStart: ISO.optional(),
  periodEnd: ISO.optional(),
  revieweeIds: z.array(z.string()).max(500).optional(),
  reviewers: z.array(REVIEW_CYCLE_REVIEWER).max(3).optional(),
  questions: z.array(z.string().min(1).max(300)).max(30).optional(),
});
export type ReviewCycle = z.infer<typeof REVIEW_CYCLE>;

/** A written policy staff must read and acknowledge. */
export const POLICY = BASE.extend({
  title: z.string(),
  version: z.string(),
  required: z.boolean(),
  acknowledged: z.number(),
  total: z.number(),
  ownerId: z.string(),
  /** The policy's written text (A5b-DOCS-07), shown in the policy panel. */
  body: z.string().max(20000).optional(),
  /** The day the current version takes effect, as YYYY-MM-DD. */
  effectiveOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** Role ids whose holders must accept the policy. Empty or absent means every staff member must. */
  mustAccept: z.array(z.string().min(1).max(60)).max(50).optional(),
});
export type Policy = z.infer<typeof POLICY>;

export const REQUISITION_STAGE = z.enum(["open", "shortlist", "offered", "filled"]);
export type RequisitionStage = z.infer<typeof REQUISITION_STAGE>;

/** A freelancer role a project needs filled. `candidates` holds freelancer ids, `neededBy` is the start day. */
export const REQUISITION = BASE.extend({
  title: z.string(),
  projectId: z.string(),
  skills: z.array(z.string()),
  hours: z.number(),
  rate: MONEY,
  stage: REQUISITION_STAGE,
  candidates: z.array(z.string()),
  neededBy: ISO,
  /** Set while an offer is out or after it is accepted. */
  freelancerId: z.string().nullable().optional(),
  workOrderId: z.string().nullable().optional(),
});
export type Requisition = z.infer<typeof REQUISITION>;
